import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createBloodRequest, useCollection } from '../hooks/useSupabase';
import { triggerBloodRequestNotifications } from '../services/notificationService';
import { KERALA_HOSPITALS, getHospitalsByDistrict, searchHospitals } from '../data/keralaHospitals';
import { BLOOD_GROUPS, URGENCY_LEVELS, DISTRICTS_KERALA, validateBloodRequest } from '../types/schema';

const BloodRequestForm = ({ onSuccess, onCancel, requestedBy }) => {
  const navigate = useNavigate();
  const { data: donors } = useCollection('donors'); // Get all donors for notification
  const [formData, setFormData] = useState({
    patientName: '',
    bloodGroup: '',
    unitsRequired: 1,
    urgencyLevel: URGENCY_LEVELS.MEDIUM,
    requiredByDate: '',
    hospital: {
      name: '',
      location: '',
      district: '',
    },
    contactPerson: {
      name: '',
      phoneNumber: '',
      relationship: 'family',
    },
    parish: '',
    additionalNotes: '',
  });

  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [newRequest, setNewRequest] = useState(null);
  const [hospitalSuggestions, setHospitalSuggestions] = useState([]);
  const [showHospitalSuggestions, setShowHospitalSuggestions] = useState(false);
  const [isCustomHospital, setIsCustomHospital] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleHospitalNameChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      hospital: {
        ...prev.hospital,
        name: value
      }
    }));

    // Show suggestions when user types
    if (value.length > 2) {
      const suggestions = searchHospitals(value).slice(0, 5);
      setHospitalSuggestions(suggestions);
      setShowHospitalSuggestions(true);
    } else {
      setShowHospitalSuggestions(false);
    }
  };

  const selectHospital = (hospital) => {
    setFormData(prev => ({
      ...prev,
      hospital: {
        name: hospital.name,
        location: hospital.location,
        district: hospital.district,
      }
    }));
    setShowHospitalSuggestions(false);
  };

  const handleDistrictChange = (e) => {
    const district = e.target.value;
    setFormData(prev => ({
      ...prev,
      hospital: {
        ...prev.hospital,
        district: district,
        name: '', // Reset hospital name when district changes
        location: ''
      }
    }));
    
    // Show hospitals in selected district
    if (district) {
      const districtHospitals = getHospitalsByDistrict(district);
      setHospitalSuggestions(districtHospitals);
    }
  };

  const checkDuplicateRequest = async (patientName, bloodGroup, hospitalName, contactPhone) => {
    const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/blood_requests`;
    const checkUrl = `${url}?patient_name=eq.${encodeURIComponent(patientName)}&blood_group=eq.${encodeURIComponent(bloodGroup)}&hospital_name=eq.${encodeURIComponent(hospitalName)}&contact_person_phone=eq.${encodeURIComponent(contactPhone)}&status=eq.active&select=id`;

    console.log('[BloodRequestForm] Checking for duplicates:', checkUrl);

    const response = await fetch(checkUrl, {
      headers: {
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      }
    });

    if (!response.ok) {
      console.log('[BloodRequestForm] Duplicate check failed:', response.status);
      return null;
    }

    const data = await response.json();
    console.log('[BloodRequestForm] Duplicate check result:', data);
    return data && data.length > 0 ? data[0] : null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form data
    const requestData = {
      ...formData,
      unitsRequired: parseInt(formData.unitsRequired),
      requestedBy: requestedBy || 'anonymous',
    };

    const validationErrors = validateBloodRequest(requestData);

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setErrors([]);

    // Check for duplicate request
    const existingRequest = await checkDuplicateRequest(
      formData.patientName,
      formData.bloodGroup,
      formData.hospital.name,
      formData.contactPerson.phoneNumber
    );

    if (existingRequest) {
      setLoading(false);
      toast.error('A similar blood request already exists for this patient at this hospital. Please check existing requests or update your request.');
      setErrors(['Duplicate request found. A similar blood request is already active.']);
      return;
    }

    try {
      const result = await createBloodRequest(requestData);
      console.log('Blood request created successfully:', result);
      
      // Trigger notifications to eligible donors
      try {
        if (donors && donors.length > 0) {
          const notifications = await triggerBloodRequestNotifications(
            { ...requestData, id: result.id }, 
            donors
          );
          console.log(`Sent notifications to ${notifications.length} eligible donors`);
        }
      } catch (notificationError) {
        console.error('Failed to send notifications:', notificationError);
        // Don't fail the request creation if notifications fail
      }
      
      setNewRequest(result);
      setShowSuccess(true);
      if (onSuccess) {
        onSuccess(result);
      }
    } catch (error) {
      console.error('Failed to create blood request:', error);
      setErrors(['Failed to create blood request. Please try again.']);
    } finally {
      setLoading(false);
    }
  };

  const getUrgencyColor = (level) => {
    switch (level) {
      case URGENCY_LEVELS.CRITICAL: return 'text-red-600';
      case URGENCY_LEVELS.HIGH: return 'text-orange-600';
      case URGENCY_LEVELS.MEDIUM: return 'text-yellow-600';
      case URGENCY_LEVELS.LOW: return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  if (showSuccess && newRequest) {
    const urgencyConfig = {
      critical: { color: 'bg-red-600', text: 'Critical - Emergency', icon: '🚨' },
      high: { color: 'bg-orange-500', text: 'High Priority', icon: '⚡' },
      medium: { color: 'bg-yellow-500', text: 'Medium Priority', icon: '📋' },
      low: { color: 'bg-blue-500', text: 'Standard', icon: '📌' }
    };
    const urgency = urgencyConfig[newRequest.urgency_level || newRequest.urgencyLevel] || urgencyConfig.medium;
    const requiredBy = newRequest.required_by_date || newRequest.requiredByDate;
    const formattedDate = requiredBy ? new Date(requiredBy).toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : 'As soon as possible';
    const formattedTime = requiredBy ? new Date(requiredBy).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '';

    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-crimson-50 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Success Header */}
          <div className="text-center mb-8">
            <div className="mx-auto w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-600 rounded-full flex items-center justify-center shadow-lg shadow-green-200 mb-4">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Request Posted Successfully!</h1>
            <p className="text-gray-600">Your blood request is now live and eligible donors are being notified.</p>
          </div>

          {/* Priority Badge */}
          <div className={`${urgency.color} text-white rounded-2xl p-4 mb-6 flex items-center gap-3 shadow-lg`}>
            <span className="text-2xl">{urgency.icon}</span>
            <div>
              <p className="font-semibold text-lg">{urgency.text}</p>
              <p className="text-white/80 text-sm">Blood needed urgently</p>
            </div>
          </div>

          {/* Main Request Card */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden mb-6">
            {/* Patient Info Header */}
            <div className="bg-gradient-to-r from-crimson-600 to-crimson-700 text-white p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-crimson-100 text-sm mb-1">Patient Name</p>
                  <h2 className="text-2xl font-bold">{newRequest.patient_name || newRequest.patientName}</h2>
                </div>
                <div className="text-center bg-white/20 rounded-xl p-3 backdrop-blur-sm">
                  <p className="text-xs text-crimson-100 mb-1">Blood Group</p>
                  <p className="text-3xl font-black">{newRequest.blood_group || newRequest.bloodGroup}</p>
                </div>
              </div>
            </div>

            {/* Details Grid */}
            <div className="p-6">
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 rounded-xl p-4 text-center">
                  <p className="text-gray-500 text-sm mb-1">Units Required</p>
                  <p className="text-2xl font-bold text-gray-900">{newRequest.units_required || newRequest.unitsRequired}</p>
                  <p className="text-gray-400 text-xs">unit(s)</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 text-center">
                  <p className="text-gray-500 text-sm mb-1">Required By</p>
                  <p className="text-lg font-bold text-gray-900">{formattedDate}</p>
                  {formattedTime && <p className="text-gray-400 text-xs">{formattedTime}</p>}
                </div>
              </div>

              {/* Hospital Info */}
              <div className="border-t border-gray-100 pt-6 mb-6">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  Hospital Information
                </h3>
                <div className="bg-blue-50 rounded-xl p-4">
                  <p className="font-semibold text-gray-900 text-lg mb-1">{newRequest.hospital_name || newRequest.hospital?.name}</p>
                  <p className="text-gray-600 text-sm">{newRequest.hospital_location || newRequest.hospital?.location}</p>
                  <p className="text-gray-500 text-sm">{newRequest.hospital_district || newRequest.hospital?.district}</p>
                </div>
              </div>

              {/* Contact Person */}
              <div className="border-t border-gray-100 pt-6 mb-6">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Contact Person
                </h3>
                <div className="bg-green-50 rounded-xl p-4">
                  <p className="font-semibold text-gray-900">{newRequest.contact_person_name || newRequest.contactPerson?.name}</p>
                  <p className="text-gray-600 text-sm flex items-center gap-2 mt-1">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    {newRequest.contact_person_phone || newRequest.contactPerson?.phoneNumber}
                  </p>
                  <p className="text-gray-500 text-xs mt-1">
                    {newRequest.contact_person_relationship || newRequest.contactPerson?.relationship}
                  </p>
                </div>
              </div>

              {/* Additional Notes */}
              {(newRequest.additional_notes || newRequest.additionalNotes) && (
                <div className="border-t border-gray-100 pt-6 mb-6">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Additional Notes</h3>
                  <p className="text-gray-700 bg-yellow-50 rounded-xl p-4 text-sm">
                    {newRequest.additional_notes || newRequest.additionalNotes}
                  </p>
                </div>
              )}

              {/* Status & Next Steps */}
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  <span className="text-sm font-medium text-gray-700">Request is Active</span>
                </div>
                <p className="text-sm text-gray-600">
                  Eligible donors matching blood group <strong>{newRequest.blood_group || newRequest.bloodGroup}</strong> in <strong>{newRequest.hospital_district || newRequest.hospital?.district}</strong> area will be notified.
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button
              onClick={() => navigate('/requests')}
              className="btn btn-primary btn-lg w-full flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              View All Requests
            </button>
            <button
              onClick={() => {
                setShowSuccess(false);
                setNewRequest(null);
              }}
              className="btn btn-outline btn-lg w-full flex items-center justify-center gap-2 border-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create New Request
            </button>
          </div>

          {/* Help Text */}
          <p className="text-center text-gray-500 text-sm mt-6">
            Request ID: <span className="font-mono text-gray-700">{newRequest.id}</span>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="form-shell">
      <div className="section-card">
      <h2 className="section-title mb-2">Request Blood Donation</h2>
      <p className="section-subtitle mb-6">Fill this form so nearby eligible donors can be notified quickly.</p>
      
      {errors.length > 0 && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl">
          <h3 className="text-red-800 font-medium mb-2">Please fix the following errors:</h3>
          <ul className="list-disc list-inside text-red-700">
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Patient Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="patientName" className="field-label">
              Patient Name *
            </label>
            <input
              type="text"
              id="patientName"
              name="patientName"
              value={formData.patientName}
              onChange={handleInputChange}
              required
              className="input-field"
              placeholder="Enter patient's name"
            />
          </div>

          <div>
            <label htmlFor="bloodGroup" className="field-label">
              Blood Group Required *
            </label>
            <select
              id="bloodGroup"
              name="bloodGroup"
              value={formData.bloodGroup}
              onChange={handleInputChange}
              required
              className="input-field"
            >
              <option value="">Select Blood Group</option>
              {BLOOD_GROUPS.map(group => (
                <option key={group} value={group}>{group}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="unitsRequired" className="field-label">
              Units Required *
            </label>
            <select
              id="unitsRequired"
              name="unitsRequired"
              value={formData.unitsRequired}
              onChange={handleInputChange}
              required
              className="input-field"
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                <option key={num} value={num}>{num} unit{num > 1 ? 's' : ''}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="requiredByDate" className="field-label">
              Required By Date *
            </label>
            <input
              type="date"
              id="requiredByDate"
              name="requiredByDate"
              value={formData.requiredByDate}
              onChange={handleInputChange}
              required
              min={new Date().toISOString().split('T')[0]}
              className="input-field"
            />
          </div>

          <div>
            <label htmlFor="urgencyLevel" className="field-label">
              Urgency Level *
            </label>
            <select
              id="urgencyLevel"
              name="urgencyLevel"
              value={formData.urgencyLevel}
              onChange={handleInputChange}
              required
              className="input-field"
            >
              <option value={URGENCY_LEVELS.LOW} className={getUrgencyColor(URGENCY_LEVELS.LOW)}>
                Low - Can wait a few days
              </option>
              <option value={URGENCY_LEVELS.MEDIUM} className={getUrgencyColor(URGENCY_LEVELS.MEDIUM)}>
                Medium - Needed within 24-48 hours
              </option>
              <option value={URGENCY_LEVELS.HIGH} className={getUrgencyColor(URGENCY_LEVELS.HIGH)}>
                High - Needed within 12-24 hours
              </option>
              <option value={URGENCY_LEVELS.CRITICAL} className={getUrgencyColor(URGENCY_LEVELS.CRITICAL)}>
                Critical - Needed immediately
              </option>
            </select>
          </div>
        </div>

        {/* Hospital Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-800">Hospital Information</h3>
          
          <div>
            <label htmlFor="hospital.district" className="field-label">
              Hospital District *
            </label>
            <select
              id="hospital.district"
              name="hospital.district"
              value={formData.hospital.district}
              onChange={handleDistrictChange}
              required
              className="input-field"
            >
              <option value="">Select District</option>
              {DISTRICTS_KERALA.map(district => (
                <option key={district} value={district}>{district}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2 mb-2">
            <input
              type="checkbox"
              id="customHospital"
              checked={isCustomHospital}
              onChange={(e) => {
                setIsCustomHospital(e.target.checked);
                setShowHospitalSuggestions(false);
                if (e.target.checked) {
                  setFormData(prev => ({
                    ...prev,
                    hospital: { ...prev.hospital, name: '', location: '' }
                  }));
                }
              }}
              className="w-4 h-4 text-crimson-600 rounded border-gray-300 focus:ring-crimson-500"
            />
            <label htmlFor="customHospital" className="text-sm text-gray-700 cursor-pointer">
              Hospital not in list — enter custom name
            </label>
          </div>

          <div className="relative">
            <label htmlFor="hospital.name" className="field-label">
              Hospital Name *
            </label>
            <input
              type="text"
              id="hospital.name"
              name="hospital.name"
              value={formData.hospital.name}
              onChange={handleHospitalNameChange}
              onFocus={() => !isCustomHospital && formData.hospital.name.length > 2 && setShowHospitalSuggestions(true)}
              required
              className="input-field"
              placeholder={isCustomHospital ? "Enter hospital name" : "Start typing hospital name..."}
            />
            
            {!isCustomHospital && showHospitalSuggestions && hospitalSuggestions.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-xl shadow-soft max-h-60 overflow-y-auto">
                {hospitalSuggestions.map((hospital, index) => (
                  <div
                    key={index}
                    onClick={() => selectHospital(hospital)}
                    className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                  >
                    <div className="font-medium text-gray-800">{hospital.name}</div>
                    <div className="text-sm text-gray-600">
                      {hospital.location}, {hospital.district}
                      {hospital.hasBloodBank && (
                        <span className="ml-2 text-green-600">• Has Blood Bank</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label htmlFor="hospital.location" className="field-label">
              Hospital Location/Address *
            </label>
            <input
              type="text"
              id="hospital.location"
              name="hospital.location"
              value={formData.hospital.location}
              onChange={handleInputChange}
              required
              className="input-field"
              placeholder="Hospital address or landmark"
            />
          </div>
        </div>

        {/* Contact Person Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-800">Contact Person (Bystander)</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="contactPerson.name" className="field-label">
                Contact Person Name *
              </label>
              <input
                type="text"
                id="contactPerson.name"
                name="contactPerson.name"
                value={formData.contactPerson.name}
                onChange={handleInputChange}
                required
                className="input-field"
                placeholder="Name of person to contact"
              />
            </div>

            <div>
              <label htmlFor="contactPerson.phoneNumber" className="field-label">
                Contact Phone Number *
              </label>
              <input
                type="tel"
                id="contactPerson.phoneNumber"
                name="contactPerson.phoneNumber"
                value={formData.contactPerson.phoneNumber}
                onChange={handleInputChange}
                required
                className="input-field"
                placeholder="10-digit phone number"
              />
            </div>
          </div>

          <div>
            <label htmlFor="contactPerson.relationship" className="field-label">
              Relationship to Patient
            </label>
            <select
              id="contactPerson.relationship"
              name="contactPerson.relationship"
              value={formData.contactPerson.relationship}
              onChange={handleInputChange}
              className="input-field"
            >
              <option value="family">Family Member</option>
              <option value="friend">Friend</option>
              <option value="colleague">Colleague</option>
              <option value="bystander">Bystander</option>
              <option value="medical_staff">Medical Staff</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>

        {/* Additional Information */}
        <div>
          <label htmlFor="parish" className="field-label">
            Parish (if patient belongs to one)
          </label>
          <input
            type="text"
            id="parish"
            name="parish"
            value={formData.parish}
            onChange={handleInputChange}
            className="input-field"
            placeholder="Parish name (optional)"
          />
        </div>

        <div>
          <label htmlFor="additionalNotes" className="field-label">
            Additional Notes
          </label>
          <textarea
            id="additionalNotes"
            name="additionalNotes"
            value={formData.additionalNotes}
            onChange={handleInputChange}
            rows={3}
            className="input-field"
            placeholder="Any additional information about the patient's condition or requirements..."
          />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 pt-4">
          <button
            type="submit"
            disabled={loading}
            className="btn-primary flex-1 justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Posting Request...' : 'Post Blood Request'}
          </button>
          
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="btn-outline flex-1 justify-center"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
        <h3 className="text-yellow-800 font-medium mb-2">Important:</h3>
        <ul className="text-yellow-700 text-sm space-y-1">
          <li>• This request will be visible to registered blood donors in your area</li>
          <li>• Requests automatically expire after 7 days</li>
          <li>• Please ensure all contact information is accurate</li>
          <li>• You'll be notified when donors express interest</li>
        </ul>
      </div>
      </div>
    </div>
  );
};

export default BloodRequestForm;
